import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { getMe, logout, setActivePropertyId } from '@/services/api.client';
import type { MeProfile } from '@/types';

interface AuthContextValue {
  user: MeProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  refetch: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Dev bypass — set VITE_DEV_USER=resident|manager|guard in client/.env to skip login.
// Each stub matches the shape returned by GET /me for the corresponding seed user.
// Never defined in production builds (import.meta.env is tree-shaken).
// ---------------------------------------------------------------------------
const DEV_USER: MeProfile | null = (() => {
  const devRole = import.meta.env['VITE_DEV_USER'];
  if (devRole === 'resident') {
    return {
      id: 'd1c6d16e-764e-46d7-a300-857a7127e10d',
      email: 'resident@runda.local',
      fullName: 'Dev Resident',
      phone: null,
      platformRole: 'none' as const,
      status: 'active' as const,
      emailVerified: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: '2026-04-08T17:21:50.460Z',
      updatedAt: new Date().toISOString(),
      propertyRoles: [
        {
          propertyId: '00000000-0000-0000-0000-000000000002',
          unitId: '00000000-0000-0000-0000-000000000005',
          role: 'resident' as const,
        },
      ],
    };
  }
  if (devRole === 'manager') {
    return {
      id: 'a1b2c3d4-0000-0000-0000-000000000010',
      email: 'manager@runda.local',
      fullName: 'Dev Manager',
      phone: null,
      platformRole: 'none' as const,
      status: 'active' as const,
      emailVerified: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: '2026-04-08T17:21:50.460Z',
      updatedAt: new Date().toISOString(),
      propertyRoles: [
        {
          propertyId: '00000000-0000-0000-0000-000000000002',
          role: 'property_manager' as const,
        },
      ],
    };
  }
  if (devRole === 'enterprise') {
    return {
      id: 'e1000000-0000-0000-0000-000000000001',
      email: 'eta@runda.local',
      fullName: 'Dev Enterprise Admin',
      phone: null,
      platformRole: 'none' as const,
      status: 'active' as const,
      emailVerified: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: '2026-04-08T17:21:50.460Z',
      updatedAt: new Date().toISOString(),
      propertyRoles: [
        {
          propertyId: '00000000-0000-0000-0000-000000000002',
          unitId: '00000000-0000-0000-0000-000000000010',
          role: 'enterprise_tenant_admin' as const,
        },
      ],
    };
  }
  if (devRole === 'employee') {
    return {
      id: 'emp10000-0000-0000-0000-000000000001',
      email: 'employee@runda.local',
      fullName: 'Dev Employee',
      phone: null,
      platformRole: 'none' as const,
      status: 'active' as const,
      emailVerified: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: '2026-04-08T17:21:50.460Z',
      updatedAt: new Date().toISOString(),
      propertyRoles: [
        {
          propertyId: '00000000-0000-0000-0000-000000000002',
          role: 'employee' as const,
        },
      ],
    };
  }
  if (devRole === 'org_admin') {
    return {
      id: 'oa000000-0000-0000-0000-000000000001',
      email: 'orgadmin@securegate.local',
      fullName: 'Dev Org Admin',
      phone: null,
      platformRole: 'none' as const,
      status: 'active' as const,
      emailVerified: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: '2026-04-08T17:21:50.460Z',
      updatedAt: new Date().toISOString(),
      propertyRoles: [
        {
          propertyId: '00000000-0000-0000-0000-000000000002',
          organisationId: '00000000-0000-0000-0000-000000000001',
          role: 'org_admin' as const,
        },
      ],
    };
  }
  if (devRole === 'super_admin') {
    return {
      id: 'sa000000-0000-0000-0000-000000000001',
      email: 'superadmin@securegate.local',
      fullName: 'Dev Super Admin',
      phone: null,
      platformRole: 'super_admin' as const,
      status: 'active' as const,
      emailVerified: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: '2026-04-08T17:21:50.460Z',
      updatedAt: new Date().toISOString(),
      propertyRoles: [],
    };
  }
  if (devRole === 'guard') {
    return {
      id: 'gd000000-0000-0000-0000-000000000001',
      email: 'guard@runda.local',
      fullName: 'Dev Guard',
      phone: null,
      platformRole: 'none' as const,
      status: 'active' as const,
      emailVerified: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: '2026-04-08T17:21:50.460Z',
      updatedAt: new Date().toISOString(),
      propertyRoles: [
        {
          propertyId: '00000000-0000-0000-0000-000000000002',
          role: 'guard' as const,
        },
      ],
    };
  }
  return null;
})();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['me'],
    queryFn: DEV_USER
      ? () => Promise.resolve(DEV_USER)
      : async () => {
          // Dynamically check for device session on every call — localStorage
          // may have been updated after device pairing (the ternary approach
          // captured the value at mount time and never re-evaluated).
          const deviceToken = localStorage.getItem('sg_device_session');
          if (deviceToken) {
            // Mode B device session — call /guard/me instead of /me
            const { getGuardDeviceMe } = await import('@/services/api.client');
            const device = await getGuardDeviceMe();
            // Construct a MeProfile-compatible object so the rest of the app works
            return {
              id: device.guardId ?? 'device-guard',
              email: '',
              fullName: device.guardName ?? 'Guard',
              phone: null,
              platformRole: 'none' as const,
              propertyRoles: [
                {
                  propertyId: device.propertyId,
                  role: 'guard' as const,
                },
              ],
              status: 'active' as const,
              emailVerified: true,
              lastLoginAt: device.shiftStartedAt,
              createdAt: '',
              updatedAt: '',
            } satisfies MeProfile;
          }
          return getMe();
        },
    // Retry on network errors / 5xx but never on 4xx auth errors.
    // retry: false caused users on mobile data to be kicked to /login
    // whenever a brief signal drop failed the initial /me request.
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status < 500) return false; // 401/403 — not authenticated
      return true; // network error or 5xx — transient, retry
    },
    retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 5_000),
    staleTime: 5 * 60_000,
  });

  // Keep the Axios X-Property-Id header in sync with the user's active property.
  // TenantMiddleware uses this on plain localhost (no subdomain available).
  // Picks the first role that has a propertyId — works for resident, PM, and supervisor.
  useEffect(() => {
    const propertyId = user?.propertyRoles.find((r) => r.propertyId)?.propertyId ?? null;
    setActivePropertyId(propertyId);
    return () => setActivePropertyId(null);
  }, [user]);

  // Redirect authenticated users from platform aliases (app.*, www.*, admin.*)
  // to their estate's subdomain (e.g. ray.securelabsystems.com).
  useEffect(() => {
    if (!user || user.platformRole === 'super_admin') return;
    const subdomain = (user.propertyRoles[0] as { subdomain?: string | null } | undefined)
      ?.subdomain;
    if (!subdomain) return;

    const host = window.location.hostname;
    const hostParts = host.split('.');
    if (hostParts.length < 3) return; // localhost or bare domain — skip
    const firstPart = hostParts[0] ?? '';
    if (!['app', 'www', 'admin'].includes(firstPart)) return; // already on a subdomain

    const baseDomain = hostParts.slice(1).join('.');
    const target = `${window.location.protocol}//${subdomain}.${baseDomain}${window.location.pathname}${window.location.search}`;
    window.location.replace(target);
  }, [user]);

  const signOut = useCallback(async () => {
    if (DEV_USER) return; // no-op in dev bypass mode
    // Invalidate the cookie on the server FIRST. If we clear the query cache
    // before the cookie is gone, React Query immediately refetches /me with a
    // still-valid cookie, restores the user, and RoleProtectedRoute never fires
    // the /login redirect (LoginPage's "already authenticated" effect then
    // bounces the user back to their dashboard). Clearing the cache after logout
    // means any triggered refetch hits a 401 → isAuthenticated stays false →
    // RoleProtectedRoute renders <Navigate to="/login"> deterministically.
    try {
      await logout();
    } catch {
      /* cookie may already be expired — ignore */
    }
    // Best-effort: unregister this browser's web push token so a shared device
    // does not keep receiving the previous user's alerts. Never blocks logout.
    try {
      const { unregisterWebPush } = await import('@/lib/push');
      await unregisterWebPush();
    } catch {
      /* push not configured or already gone — ignore */
    }
    queryClient.setQueryData(['me'], undefined);
    queryClient.clear();
    // Imperative redirect — bypasses the async React Query refetch cycle that
    // would otherwise hold `isLoading: true` (showing a spinner) for a full
    // 401 round-trip before RoleProtectedRoute fires the <Navigate> redirect.
    window.location.replace('/login');
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: user ?? null,
      isLoading,
      isAuthenticated: !!user,
      signOut,
      refetch,
    }),
    [user, isLoading, signOut, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
